import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import AssetDataService from "../services/AssetService";
import config from "../../config";

const initialState = {
  assets: [],
  error: null,
  successMessage: null,
  loading: false,
  selectedAsset: null,
  // pagination
  total: 0,
  page: 1,
  per_page: 15,
}

export const createAsset = createAsyncThunk(
  "assets/create",
  async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
      const response = await fetch(`${config.apiUrl}/assets`, {
        method: "POST",
        body: JSON.stringify(obj.values),
        headers: {
          'Accept': "application/json",
          "Content-Type": "application/json",
          'Authorization': `Bearer ${obj.token}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data)
      } else {
        return fulfillWithValue(data)
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
);

export const retrieveAssets = createAsyncThunk(
  "assets/retrieve",
  async (obj = {}, thunkAPI) => {
    try {
      // support retrieveAssets({ page, per_page, search, name, notes })
      const params = { ...(obj || {}) };
      const token = obj?.token || null;
      if (token) delete params.token;

      const response = await AssetDataService.getAll(params);
      const payload = response?.data;

      if (Array.isArray(payload)) {
        return { data: payload };
      }

      if (Array.isArray(payload?.data)) {
        return { data: payload.data, total: payload.total ?? payload.data.length, per_page: payload.per_page ?? null, current_page: payload.current_page ?? 1 };
      }

      if (Array.isArray(payload?.data?.data)) {
        return { data: payload.data.data, total: payload.data.total ?? payload.data.data.length, per_page: payload.data.per_page ?? null, current_page: payload.data.current_page ?? 1 };
      }

      return { data: [] };
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message ?? "Unable to fetch assets");
    }
  }
);

export const updateAsset = createAsyncThunk(
  "assets/update",
  async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
      const response = await fetch(`${config.apiUrl}/assets/${obj.payload.id}`, {
        method: "PUT",
        body: JSON.stringify(obj.payload),
        headers: {
          'Accept': "application/json",
          "Content-Type": "application/json",
          'Authorization': `Bearer ${obj.token}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data)
      } else {
        return fulfillWithValue(data)
      }

    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
);

export const deleteAsset = createAsyncThunk(
  "assets/delete",
  async ({ id }) => {
    const response = await AssetDataService.remove(id);
    return response.data;
  }
);
const assetSlice = createSlice({
  name: "asset",
  initialState,
  reducers: {
    selectAsset: (state, action) => {
      state.selectedAsset = action.payload
    }
  },
  extraReducers: {
    // retrieveAssets actions
    [retrieveAssets.pending]: (state, action) => {
      state.loading = true
      state.successMessage = null
      state.error = null
    },
    [retrieveAssets.fulfilled]: (state, action) => {
      state.loading = false
      state.assets = action.payload?.data ?? [];
      state.total = action.payload?.total ?? (Array.isArray(action.payload?.data) ? action.payload.data.length : 0);
      state.page = action.payload?.current_page ?? state.page;
      state.per_page = action.payload?.per_page ?? state.per_page;
      state.successMessage = null
      state.error = null
    },
    [retrieveAssets.rejected]: (state, action) => {
      state.loading = false
      state.successMessage = null
      state.error = action.payload
    },

    // createAsset actions
    [createAsset.pending]: (state, action) => {
      state.loading = true;
      state.successMessage = null
      state.error = null
    },
    [createAsset.fulfilled]: (state, action) => {
      state.assets = [...state.assets, action.payload.data];
      state.loading = false
      state.successMessage = action.payload.message
      state.error = null
    },
    [createAsset.rejected]: (state, action) => {
      state.loading = false
      state.successMessage = null
      state.error = action.payload.message
    },

    // updateAsset actions
    [updateAsset.pending]: (state, action) => {
      state.loading = true;
      state.successMessage = null
      state.error = null
    },
    [updateAsset.fulfilled]: (state, action) => {
      const index = state.assets.findIndex(asset => asset.id === action.payload.data.id);
      state.assets[index] = {
        ...state.assets[index],
        ...action.payload.data,
      };
      state.loading = false;
      state.successMessage = action.payload.message
      state.error = null
    },
    [updateAsset.rejected]: (state, action) => {
      state.loading = false
      state.successMessage = null
      state.error = action.payload
    },

    // deleteAsset actions
    [deleteAsset.pending]: (state, action) => {
      state.loading = true;
      state.successMessage = null
      state.error = null
    },
    [deleteAsset.fulfilled]: (state, action) => {
      let index = state.assets.findIndex(({ id }) => id === action.payload.data.id);
      state.assets.splice(index, 1);
      state.loading = false
      state.successMessage = action.payload.message
      state.error = null
    },
    [deleteAsset.rejected]: (state, action) => {
      state.loading = false;
      state.successMessage = null
      state.error = action.payload;
    },
  },
});

const { reducer } = assetSlice;
export const { selectAsset } = assetSlice.actions
export default reducer;